import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import SearchIcon from '@mui/icons-material/Search'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip, { QNA_ANSWER_STATUS_CHIP } from '../../component/StatusChip'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseNumberParam } from '../../utils/listPageState'

const DEFAULT_SEARCH_SELECT = {
    searchDeviceType: '',
    searchType: 0,
    searchStatus: 99,
    searchUserId: '',
}

const SEARCH_SELECT_PARAMS = {
    searchDeviceType: {
        param: 'deviceType',
        defaultValue: DEFAULT_SEARCH_SELECT.searchDeviceType,
    },
    searchType: {
        param: 'type',
        defaultValue: DEFAULT_SEARCH_SELECT.searchType,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
    searchUserId: {
        param: 'userId',
        defaultValue: DEFAULT_SEARCH_SELECT.searchUserId,
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const getAppQnaTypeLabel = (type) => {
    switch (Number(type)) {
        case 1:
            return '일반문의'
        case 2:
            return '기능개선'
        case 3:
            return '버그신고'
        case 4:
            return '광고문의'
        default:
            return '-'
    }
}

const getAnswerStatus = (status) => Number(Boolean(status))

export default function AppQnas() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const deleteInFlightRef = useRef(false)

    const getData = useCallback(async () => {
        setLoading(true)
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appQna/getList', {
                page: curPage, offset: offset, searchText: searchQuery, searchSelect: searchSelect
            })
            // console.log('data: ',data.data)
            if (data.code === 200) {
                setTotalCount(data.data.count)
                setParams(data.data.rows)
                setListErrorMessage('')
            } else if (data.code === 403) {
                setLoading(false)
                history.push('/login')
                return
            } else {
                setTotalCount(0)
                setParams([])
                setListErrorMessage(data.message || 'App 문의 목록을 불러오지 못했습니다.')
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setTotalCount(0)
            setParams([])
            setListErrorMessage('App 문의 목록을 불러오지 못했습니다.')
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 문의 목록을 불러오지 못했습니다.')
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handleUserFilterClick = (row, clickedLabel) => {
        if (actionLoading) {
            return
        }

        const nextSearchText = clickedLabel || row.user?.nickName || row.user?.username || ''

        setSearchText(nextSearchText)
        setSearchQuery(nextSearchText)
        setCurPage(1)
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
            searchUserId: row.userId,
        })
    }

    const resetFilters = () => {
        if (actionLoading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    const updateSearchSelect = (e) => {
        if (actionLoading) {
            return
        }

        setCurPage(1)
        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    // 검색어와 선택 필터 중 하나라도 기본값과 다르면 검색 결과 없음 문구를 보여준다.
    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        return Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? ''
            const normalizedDefaultValue = defaultValue ?? ''

            return String(currentValue) !== String(normalizedDefaultValue)
        })
    }

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="App 문의 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={getData} disabled={actionLoading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 App 문의가 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={actionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="접수된 App 문의가 없습니다."
                description="사용자 문의가 접수되면 이 목록에 표시됩니다."
            />
        )
    }

    const searchButtonClick = () => {
        if (actionLoading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage !== 1) {
            setCurPage(1)
        }

        if (searchQuery !== trimmedSearchText) {
            setSearchQuery(trimmedSearchText)
            return
        }

        if (curPage === 1) {
            getData()
        }
    }

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appQna/delete', { qnaId: id })
            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('App 문의를 삭제했습니다.')

                if (params.length === 1 && curPage > 1) {
                    setCurPage(curPage - 1)
                } else {
                    await getData()
                }
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 문의를 삭제하지 못했습니다.')
        } finally {
            setActionLoading(false)
            deleteInFlightRef.current = false
        }
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>App 문의 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={actionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="유저ID/이름/닉네임/이메일/내용"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                disabled={actionLoading}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={actionLoading}><SearchIcon /></IconButton>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            <TextField
                                label="기기"
                                select
                                name="searchDeviceType"
                                value={searchSelect.searchDeviceType ?? ''}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value={''}>전체</MenuItem>
                                <MenuItem value={'and'}>Android</MenuItem>
                                <MenuItem value={'ios'}>iOS</MenuItem>
                            </TextField>
                            <TextField
                                label="문의유형"
                                select
                                name="searchType"
                                value={searchSelect.searchType ?? 0}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value={0}>전체</MenuItem>
                                <MenuItem value={1}>일반문의</MenuItem>
                                <MenuItem value={2}>기능개선</MenuItem>
                                <MenuItem value={3}>버그신고</MenuItem>
                                <MenuItem value={4}>광고문의</MenuItem>
                            </TextField>
                            <TextField
                                label="답변상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus ?? 99}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value={99}>전체</MenuItem>
                                <MenuItem value={0}>미답변</MenuItem>
                                <MenuItem value={1}>답변</MenuItem>
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>
                    {/* 테이블 */}
                    <Box sx={{ opacity: actionLoading ? 0.5 : 1, pointerEvents: actionLoading ? 'none' : 'auto' }}>
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: 800 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>이름</TableCell>
                                <TableCell>닉네임</TableCell>
                                <TableCell>기기타입</TableCell>
                                <TableCell>문의종류</TableCell>
                                <TableCell>내용</TableCell>
                                <TableCell align="center">답변여부</TableCell>
                                <TableCell align="center">등록일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? params.map((row) => (
                                <TableRow key={row.qnaId}>
                                    <TableCell>{row.rownum}</TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            sx={{ minWidth: 0, p: 0, justifyContent: 'flex-start', textTransform: 'none', color: 'text.primary' }}
                                            onClick={() => handleUserFilterClick(row, row.user.username)}
                                            disabled={actionLoading}
                                        >
                                            {row.user.username}
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            sx={{ minWidth: 0, p: 0, justifyContent: 'flex-start', textTransform: 'none', color: 'text.primary' }}
                                            onClick={() => handleUserFilterClick(row, row.user.nickName)}
                                            disabled={actionLoading}
                                        >
                                            {row.user.nickName}
                                        </Button>
                                    </TableCell>
                                    <TableCell>{row.user.deviceType}</TableCell>
                                    <TableCell>{getAppQnaTypeLabel(row.type)}</TableCell>
                                    <TableCell className="listTableLimitText">{row.content}</TableCell>
                                    <TableCell align="center"><StatusChip status={getAnswerStatus(row.status)} config={QNA_ANSWER_STATUS_CHIP} /></TableCell>
                                    <TableCell align="center">{row.regTime}</TableCell>
                                    <TableCell align="right">
                                        <ListActionGroup>
                                            <EditIcon path={'appQna'} id={row.qnaId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} disabled={actionLoading}/>
                                            <DeleteAlertDialog handleDelete={handleDelete} id={row.qnaId} disabled={actionLoading}/>
                                        </ListActionGroup>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    </Box>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
